import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { changeFormField, setFieldError, setNewForm, updateFormProperties } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import InputSMS from 'apps/auth/components/form/InputSMS';
import Form from 'apps/auth/components/form-multi/Form';
import { smsConfirmationToFields } from 'apps/auth/components/form-multi/utils';
import { PASSCODE_SCREEN } from 'apps/auth/components/registration/constants';
import DuplicatedRegistrationRestrictionModal from 'apps/auth/components/utils/DuplicatedRegistrationRestrictionModal';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import { LOGIN_REGISTRATION_FORM_NAME, SMS_CONFIRMATION_FORM_NAME } from 'apps/auth/constants/formName';
import { BIRTHDAY_CHECK_ACTION, LOGIN } from 'apps/auth/constants/UrlConstants';
import { CANADA, USA } from 'apps/auth/constants/WellKnownCountries';
import { ERR_DUPLICATED_REGISTRATION } from 'apps/auth/constants/WellKnownErrorCodes';
import { smsConfirmation } from 'apps/auth/helpers/customPropTypes';
import update from 'apps/auth/helpers/Immutability';
import { addZero, resolveArguments } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { useLibPhonenumberFormatter } from 'apps/auth/hooks/useLibPhonenumberFormatter';

const RESET_INTERVAL = 59;

function useInterval(callback, delay) {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

const SmsConfirmation = ({ smsLength = 5, confirmAction = '/confirm.action', resendAction = '/resend.action', ...props }) => {
    const { phone, userCountryName } = useAuth();
    const fields = useFields(SMS_CONFIRMATION_FORM_NAME);
    const { termsOfUseCode, smsResendBtn, smsResendTitle, headerTitle, headerGrayTitle } = usePageMessages();
    const { hideButton, phoneCountryId } = useExtra();

    const interval = props.smsConfirmation?.resendTime || RESET_INTERVAL;
    const minutes = Math.floor(interval / 60);
    const seconds = interval - minutes * 60;
    const [resentTime, setResetTime] = useState(`${addZero(minutes)}:${addZero(seconds)}`);
    const [showResend, setShowResend] = useState(false);
    const [isExceedLimitsError, setIsExceedLimitsError] = useState(false);
    const submitRef = useRef(null);
    const resetInterval = useRef(interval);
    const showTerms = phoneCountryId !== USA && phoneCountryId !== CANADA;
    const { ready, formatIntlPhoneNumber } = useLibPhonenumberFormatter();
    const formattedNumber = ready ? formatIntlPhoneNumber(phone, userCountryName) : `+${phone}`;

    useEffect(() => {
        resetInterval.current = interval;
        props.setNewForm(SMS_CONFIRMATION_FORM_NAME, smsConfirmationToFields(props.smsConfirmation, props.redirectUrl));
    }, [props.smsConfirmation, props.redirectUrl]);

    useEffect(() => {
        const currentCode = fields?.code?.value;
        if (currentCode && currentCode.length === smsLength) {
            submitRef.current.click();
        }
    }, [fields?.code?.value]);

    useEffect(() => {
        if (hideButton) {
            setIsExceedLimitsError(true);
        }
    }, [hideButton]);

    const calculateTime = (value) => {
        const min = Math.floor(value / 60);
        const sec = value - min * 60;
        setResetTime(`${addZero(min)}:${addZero(sec)}`);
    };

    if (!props.declineResendSms) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useInterval(() => {
            if (resetInterval.current > 0) {
                resetInterval.current -= 1;
                calculateTime(resetInterval.current);
            } else {
                setShowResend(true);
                const newTime = props.resetInterval || RESET_INTERVAL;
                calculateTime(newTime);
            }
        }, 1000);
    }

    const submitHandler = (fields, json) => {
        if (json?.extra?.smsConfirmation) {
            if (json?.extra?.smsConfirmation?.objectType === 'VERIFY_KNOW_USER_INFO') {
                props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, BIRTHDAY_CHECK_ACTION);
                return;
            }
            resetInterval.current = json?.extra?.smsConfirmation?.resendTime || RESET_INTERVAL;
        }
        if (json.messages) {
            props.updatePageMessages(json.messages);
        }

        if (json?.extra?.createPasscode) {
            props.setScreen(PASSCODE_SCREEN);
        }
        if (json?.errors) {
            Object.keys(json.errors).forEach((name) => {
                if (json.errors[name]['code'] === ERR_DUPLICATED_REGISTRATION) {
                    props.toggleModal(true, <DuplicatedRegistrationRestrictionModal url={LOGIN} />, false);
                } else {
                    props.setFieldError(SMS_CONFIRMATION_FORM_NAME, name, json.errors[name]);
                }
            });
        }
        if (props.isProfileFlow && json.extra?.redirectUrl && json.messages?.successMessage) {
            props.modalRef.current?.close();
            props.toggleModalWithTimer(true, json.messages.successMessage, () => {
                window.location.href = json.extra.redirectUrl;
            });
        }
        props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, confirmAction, true);
    };

    const clearErrorAndValue = (oldField) => {
        const newFieldCode = update(oldField, {
            value: { $set: null },
            isError: { $set: false },
            errorMessage: { $set: null },
        });
        props.changeFormField(SMS_CONFIRMATION_FORM_NAME, newFieldCode);
    };

    const resendClick = (e) => {
        e.preventDefault();

        const { value, isError } = fields?.code;
        if (value || isError) {
            const fieldCode = fields?.code;
            clearErrorAndValue(fieldCode);
        }
        if (props.customResendAction) {
            props.customResendAction();
            resetInterval.current = interval;
            setShowResend(false);
        } else {
            props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, resendAction, false).then(() => {
                submitRef.current.click();
                resetInterval.current = interval;
                setShowResend(false);
            });
        }
    };

    const beforeRedirect = () => {
        if (props.isRegistration) {
            amplitude.getInstance().logEvent('Code Entered');
        }
    };

    if (!fields?.code) {
        return <Spinner forceShow={true} />;
    }

    const resendSms = () => {
        if (props.declineResendSms) {
            return false;
        } else {
            return (
                <>
                    {showResend && !isExceedLimitsError && (
                        <Row flex justify="center">
                            <Button variant="secondary" onClick={(e) => resendClick(e)} caption={smsResendBtn} id="resend_sms_link" />
                        </Row>
                    )}
                    {!showResend && !isExceedLimitsError && (
                        <Text size="secondary" color="lightGray">
                            {props.smsResendText || smsResendTitle} {resentTime}
                        </Text>
                    )}
                </>
            );
        }
    };
    const getSubtitleText = () => {
        if (props.headerGrayText) {
            return props.headerGrayText;
        } else {
            const phoneNumber = props.formattedNumber || formattedNumber;
            return resolveArguments(headerGrayTitle, phoneNumber);
        }
    };

    return (
        <>
            <HeaderPanel title={props.headerText || headerTitle} subtitle={getSubtitleText()} />
            <Form
                formName={SMS_CONFIRMATION_FORM_NAME}
                additionalFormNames={[LOGIN_REGISTRATION_FORM_NAME]}
                onSubmitHandler={submitHandler}
                beforeRedirect={beforeRedirect}
                submitByEnter={false}
                action={confirmAction}
                getCanonicalPath={true}
                checkNotVisibleField={false}
            >
                <Row indent="24px">
                    <InputSMS field={fields?.code} mask={'9'.repeat(smsLength)} formName={SMS_CONFIRMATION_FORM_NAME} isNewInput isSmsInput />
                </Row>
                {props.isRegistration && showTerms && (
                    <Row indent="adaptive-24-32">
                        <Text
                            size="secondary"
                            color="lightGray"
                            data-testid="sms-confirmation-p-text-offer"
                            dangerouslySetInnerHTML={{ __html: termsOfUseCode }}
                        />
                    </Row>
                )}
                {resendSms()}
                <Button type="submit" variant="hidden" ref={submitRef} />
            </Form>
        </>
    );
};

SmsConfirmation.propTypes = {
    smsConfirmation: smsConfirmation.isRequired,
    redirectUrl: PropTypes.string,
    smsLength: PropTypes.number,
    resetInterval: PropTypes.number,
    termsOfUseCode: PropTypes.string,
    headerText: PropTypes.string,
    headerGrayText: PropTypes.string,
    confirmAction: PropTypes.string,
    resendAction: PropTypes.string,
    customResendAction: PropTypes.func,
};

export default connect(null, {
    setNewForm,
    setFieldError,
    updateFormProperties,
    changeFormField,
    updatePageMessages,
    toggleModal,
    addExtras,
})(SmsConfirmation);
